import React, { useState, useRef, useEffect } from 'react';
import { 
  LocationSelectorProps, 
  DublinLocation,
  DUBLIN_LOCATIONS,
  searchLocations
} from '../../../types';
import { Icon } from '../../atoms';
import './LocationSelector.css';

const LocationSelector: React.FC<LocationSelectorProps> = ({
  selectedLocations,
  onLocationsChange,
  className = ''
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filteredLocations, setFilteredLocations] = useState<DublinLocation[]>(DUBLIN_LOCATIONS);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchQuery.trim()) {
      setFilteredLocations(searchLocations(searchQuery));
    } else {
      setFilteredLocations(DUBLIN_LOCATIONS);
    }
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setIsDropdownOpen(true);
  };

  const handleInputFocus = () => {
    setIsDropdownOpen(true);
  };

  const handleLocationToggle = (location: DublinLocation) => {
    const isSelected = selectedLocations.some(selected => selected.town === location.town);
    
    if (isSelected) {
      onLocationsChange(selectedLocations.filter(selected => selected.town !== location.town));
    } else {
      onLocationsChange([...selectedLocations, location]);
    }
  };

  const handleRemoveLocation = (locationToRemove: DublinLocation) => {
    onLocationsChange(selectedLocations.filter(location => location.town !== locationToRemove.town));
  };

  const isLocationSelected = (location: DublinLocation): boolean => {
    return selectedLocations.some(selected => selected.town === location.town);
  };

  const getAvailableLocations = (): DublinLocation[] => {
    return filteredLocations.filter(location => !isLocationSelected(location));
  };

  return (
    <div className={`location-selector ${className}`} ref={dropdownRef}>
      {/* Selected Locations */}
      {selectedLocations.length > 0 && (
        <div className="selected-locations">
          <div className="selected-header">
            <span className="selected-label">Selected Locations ({selectedLocations.length})</span>
          </div>
          <div className="selected-tags">
            {selectedLocations.map(location => (
              <div key={location.town} className="location-tag">
                <span className="tag-content">
                  <span className="tag-town">{location.town}</span>
                  <span className="tag-routing">({location.routingKey})</span>
                </span>
                <button
                  type="button"
                  className="tag-remove"
                  onClick={() => handleRemoveLocation(location)}
                  title={`Remove ${location.town}`}
                >
                  <Icon type="delete" size="small" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search Input */}
      <div className="search-container">
        <div className="search-input-wrapper">
          <Icon type="search" size="small" className="search-icon" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search for Dublin locations..."
            value={searchQuery}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            className="search-input"
          />
        </div>
      </div>

      {/* Dropdown */}
      {isDropdownOpen && (
        <div className="locations-dropdown">
          <div className="dropdown-header">
            <span className="dropdown-title">Available Locations</span>
            <span className="dropdown-count">
              {getAvailableLocations().length} available
            </span>
          </div>
          
          {getAvailableLocations().length === 0 ? (
            <div className="no-results">
              {searchQuery.trim() ? 'No locations found matching your search.' : 'All locations have been selected.'}
            </div>
          ) : (
            <div className="locations-list">
              {getAvailableLocations().map(location => (
                <div
                  key={location.town}
                  className="location-item"
                  onClick={() => handleLocationToggle(location)}
                >
                  <div className="location-info">
                    <span className="location-town">{location.town}</span>
                    <span className="location-routing">{location.routingKey}</span>
                  </div>
                  <div className="location-actions">
                    <button
                      type="button"
                      className="add-button"
                      title={`Add ${location.town}`}
                    >
                      <Icon type="add" size="small" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationSelector;
